import { useRef, useEffect } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import * as tracking from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants';

// components
import { SbSharedSearchSuggestions } from '../SbSharedSearchSuggestions/SbSharedSearchSuggestions';

// styles
import styles from './SbSharedForceSearchLink.scss';

const trackSearchCorrectionClick = (term, pageType) =>
    tracking.trackEvent({
        label: term,
        category: 'search suggestion text',
        action: `${pageType === pageTypes.SEARCH ? 'classification' : 'semantic'} override clicked`,
    });

const trackAutoCorrectClick = (correctedTerm, originalTerm) =>
    tracking.trackEvent({
        category: 'search suggestion text',
        action: 'override spelling autocorrect',
        label: `${correctedTerm}|${originalTerm}`,
    });

export const SbSharedForceSearchLinkComponent = ({ itemSearch, searchTerm }) => {
    const { searchCorrections = {}, pageType } = itemSearch;
    const {
        forceSearchUri,
        originalSearchTerm,
        semanticSearchTerm,
        classificationSearchTerm,
    } = searchCorrections;
    const trackingRef = useRef({ originalSearchTerm, searchTerm, semanticSearchTerm });

    useEffect(() => {
        const correctedTerm =
            get(trackingRef, 'current.searchTerm') ||
            get(trackingRef, 'current.semanticSearchTerm');
        const originalTerm = get(trackingRef, 'current.originalSearchTerm');

        if (originalTerm && correctedTerm) {
            tracking.trackEvent({
                category: 'search suggestion text',
                action: 'spelling autocorrect',
                label: `${originalTerm}|${correctedTerm}`,
            });
        }
    }, []);

    if (originalSearchTerm && !(semanticSearchTerm || classificationSearchTerm)) {
        return (
            <span data-tn="force-search-link-spell-correct" className={styles.link}>
                <FormattedMessage
                    id="sb.SbSharedForceSearchLink.spellCorrectText"
                    defaultMessage={'Search instead for "{searchTerm}"'}
                    values={{
                        searchTerm: (
                            <a
                                href={forceSearchUri}
                                onClick={() =>
                                    trackAutoCorrectClick(searchTerm, originalSearchTerm)
                                }
                            >
                                {originalSearchTerm}
                            </a>
                        ),
                    }}
                />
            </span>
        );
    } else if (semanticSearchTerm || classificationSearchTerm) {
        return (
            <span data-tn="force-search-link-semantic" className={styles.link}>
                <FormattedMessage
                    id="sb.sharedForceSearchLink.semantic"
                    defaultMessage='Search for "{searchTerm}" in all categories'
                    values={{
                        searchTerm: (
                            <a
                                href={forceSearchUri}
                                onClick={() =>
                                    trackSearchCorrectionClick(
                                        originalSearchTerm ||
                                            semanticSearchTerm ||
                                            classificationSearchTerm,
                                        pageType
                                    )
                                }
                            >
                                {originalSearchTerm ||
                                    semanticSearchTerm ||
                                    classificationSearchTerm}
                            </a>
                        ),
                    }}
                />
            </span>
        );
    } else {
        return (
            <SbSharedSearchSuggestions
                searchTerm={searchTerm}
                itemSearch={itemSearch}
                className={styles.link}
            />
        );
    }
};
SbSharedForceSearchLinkComponent.propTypes = {
    searchTerm: PropTypes.string,
    itemSearch: PropTypes.object.isRequired,
};

export const SbSharedForceSearchLink = createFragmentContainer(SbSharedForceSearchLinkComponent, {
    itemSearch: graphql`
        fragment SbSharedForceSearchLink_itemSearch on ItemSearchQueryConnection {
            pageType
            searchCorrections {
                forceSearchUri
                originalSearchTerm
                semanticSearchTerm
                classificationSearchTerm
            }
            ...SbSharedSearchSuggestions_itemSearch
        }
    `,
});
